import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TemplatePageDefinition } from '@troia-v3/types';
import { Select } from '@/shared/components/ui/Select';
import { Icon } from '@/shared/icons';
import { useApp } from '@/contexts/AppContext';

/**
 * Origin do preview a partir do renderUrl do app (multi-tenant — sem host fixo).
 * Dev: renderUrl 'localhost' (ou *.localhost) → http + porta 4321 do render local
 * (browsers resolvem {qualquer-label}.localhost → 127.0.0.1).
 * Produção: https direto no host ({websiteId}.{renderUrl}).
 */
function buildPreviewOrigin(websiteId: string, renderUrl: string): string {
  const isLocal = renderUrl === 'localhost' || renderUrl.endsWith('.localhost');
  return isLocal
    ? `http://${websiteId}.${renderUrl}:4321`
    : `https://${websiteId}.${renderUrl}`;
}

interface PreviewFrameProps {
  websiteId: string;
  pagesDef: TemplatePageDefinition[];
  activePageId: string;
  onPageChange: (pageId: string) => void;
  /** Incrementado a cada save com sucesso — força reload do iframe. */
  reloadToken: number;
  /** Último bloco aberto no painel — o iframe rola até ele (best-effort, postMessage). */
  scrollTarget: { pageId: string; instanceId: string; token: number } | null;
}

/**
 * Preview do site em iframe ({websiteId}.{app.renderUrl}/?draft=1) — host vem do
 * renderUrl do app (multi-tenant); sem renderUrl exibe aviso no lugar do iframe.
 * - Recarrega ao salvar (reloadToken no query string).
 * - Seletor de página + toggle desktop/mobile.
 * - postMessage 'scroll-to-block' para o helper de preview do renderer.
 */
export const PreviewFrame: React.FC<PreviewFrameProps> = ({
  websiteId,
  pagesDef,
  activePageId,
  onPageChange,
  reloadToken,
  scrollTarget,
}) => {
  const { app } = useApp();
  const renderUrl = app?.renderUrl;
  const origin = renderUrl ? buildPreviewOrigin(websiteId, renderUrl) : null;
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const route = useMemo(() => {
    const p = pagesDef.find((pg) => pg.id === activePageId);
    if (p?.isDynamic && p.routePattern) return p.routePattern.replace(':slug', '_preview');
    return p?.defaultRoute || '/';
  }, [pagesDef, activePageId]);
  const src = origin ? `${origin}${route}?draft=1&_ts=${reloadToken}` : null;
  // Rótulo limpo da barra de URL do chrome do preview (host + rota + ?draft=1), sem protocolo.
  const previewUrlLabel = useMemo(
    () => (origin ? `${origin.replace(/^https?:\/\//, '')}${route}?draft=1` : ''),
    [origin, route]
  );

  const sendScroll = useCallback(() => {
    if (!origin || !scrollTarget || scrollTarget.pageId !== activePageId) return;
    iframeRef.current?.contentWindow?.postMessage(
      { source: 'troia-customizer', type: 'scroll-to-block', instanceId: scrollTarget.instanceId },
      origin
    );
  }, [scrollTarget, activePageId, origin]);

  // Bloco aberto no painel → rola o iframe (se já carregado)
  useEffect(() => { sendScroll(); }, [sendScroll]);

  return (
    <div className="h-full flex flex-col min-w-0">
      {/* Barra do preview */}
      <div
        className="flex-shrink-0 flex items-center gap-2 px-3 py-2 border-b"
        style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
      >
        <div className="w-44">
          <Select
            value={activePageId}
            onChange={onPageChange}
            options={pagesDef.map((p) => ({ label: p.name, value: p.id }))}
            data-testid="preview-page-select"
          />
        </div>

        <div
          className="flex items-center gap-0.5 ml-2 p-0.5 rounded-lg"
          style={{ backgroundColor: 'var(--theme-page-background)', border: '1px solid var(--theme-header-border)' }}
        >
          {(['desktop', 'mobile'] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDevice(d)}
              className="flex items-center justify-center w-8 h-7 rounded-md transition-colors"
              style={{
                backgroundColor: device === d ? 'var(--theme-card-background)' : 'transparent',
                color: device === d ? 'var(--theme-primary)' : 'var(--theme-muted-text)',
              }}
              title={d === 'desktop' ? 'Desktop' : 'Mobile'}
              data-testid={`preview-device-${d}`}
            >
              <Icon name={d === 'desktop' ? 'monitor' : 'smartphone'} className="h-[18px] w-[18px]" />
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {origin && (
          <a
            href={`${origin}/?draft=1`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm border"
            style={{ borderColor: 'var(--theme-card-border)', color: 'var(--theme-card-text)' }}
            data-testid="preview-open-new-tab"
          >
            <Icon name="external-link" className="w-4 h-4" />
            Abrir em nova aba
          </a>
        )}
      </div>

      {/* Preview emoldurado: moldura de navegador (chrome + barra de URL) envolvendo o iframe;
          ou aviso quando o app não tem renderUrl configurado. */}
      <div className="flex-1 overflow-auto flex justify-center p-4" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        {src ? (
          <div
            className="w-full flex flex-col overflow-hidden rounded-xl"
            style={{
              maxWidth: device === 'mobile' ? 390 : 1080,
              backgroundColor: '#ffffff',
              border: '1px solid var(--theme-input-border)',
            }}
            data-testid="preview-frame-window"
          >
            {/* Chrome do navegador: 3 dots + barra de URL (mono) */}
            <div
              className="flex-shrink-0 flex items-center gap-2 px-3 py-2 border-b"
              style={{ backgroundColor: '#eef1f5', borderColor: '#e2e8f0' }}
            >
              <div className="flex gap-[5px]">
                <span className="rounded-full" style={{ width: 9, height: 9, backgroundColor: '#cbd5e1' }} />
                <span className="rounded-full" style={{ width: 9, height: 9, backgroundColor: '#cbd5e1' }} />
                <span className="rounded-full" style={{ width: 9, height: 9, backgroundColor: '#cbd5e1' }} />
              </div>
              <div
                className="flex-1 flex items-center truncate rounded-md font-mono"
                style={{ height: 22, padding: '0 10px', fontSize: 11, color: '#64748b', backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }}
                data-testid="preview-url-bar"
              >
                {previewUrlLabel}
              </div>
            </div>
            <iframe
              ref={iframeRef}
              key={src}
              src={src}
              onLoad={sendScroll}
              title="Preview do website"
              className="flex-1 w-full border-0"
              style={{ backgroundColor: '#ffffff' }}
              data-testid="preview-frame"
            />
          </div>
        ) : (
          <div
            className="h-full w-full flex flex-col items-center justify-center gap-2 text-center px-6"
            data-testid="preview-no-render-url"
          >
            <Icon name="globe" className="w-8 h-8" style={{ color: 'var(--theme-muted-text)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
              Preview indisponível
            </p>
            <p className="text-sm max-w-md" style={{ color: 'var(--theme-muted-text)' }}>
              O app não tem <code>renderUrl</code> configurado — defina o host base do render
              (ex: website.seudominio.com) para habilitar a pré-visualização.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
